import React, { useState, useEffect } from "react";
import { createShipment, generateShipmentId } from "../lib/shipments";
import { sendPackageShippedEmail } from "../lib/emailNotifications";
import { createInvoice, generateInvoiceNumber, calculateInvoiceTotals } from "../lib/invoices";
import { getCustomerRate } from "../lib/rates";
import { getAllRequests } from "../lib/requests";
import { Modal, toast } from "./UI";

export default function CreateShipmentModal({ packages, onClose, onSaved }) {
  const [tracking,    setTracking]    = useState(generateShipmentId());
  const [notes,       setNotes]       = useState("");
  const [saving,      setSaving]      = useState(false);
  const [rate,        setRate]        = useState(null);
  const [requests,    setRequests]    = useState([]);
  const [whishNumber, setWhishNumber] = useState("+961 XX XXX XXX");

  // All packages in this shipment belong to one customer
  const customerEmail = packages[0]?.customerEmail || "";
  const customerName  = packages[0]?.customerName  || "";

  useEffect(() => {
    if (!customerEmail) return;
    Promise.all([
      getCustomerRate(customerEmail),
      getAllRequests(),
    ]).then(([r, reqs]) => {
      setRate(r);
      // Only include completed requests for packages in this shipment
      const pkgIds = packages.map(p => p.id);
      setRequests(reqs.filter(req => pkgIds.includes(req.packageId) && req.status !== "Rejected"));
    });
  }, [customerEmail]);

  const totals = rate ? calculateInvoiceTotals(null, packages, rate.ratePerKg, requests) : null;

  const save = async () => {
    if (!tracking.trim()) { toast("Tracking number required", "error"); return; }
    setSaving(true);
    try {
      // Create shipment
      const shipmentId = await createShipment(packages.map(p => p.id), tracking.trim(), notes);

      // Auto-generate invoice
      const invoiceNumber = generateInvoiceNumber();
      await createInvoice({
        invoiceNumber,
        shipmentId,
        shipmentTracking: tracking.trim(),
        customerEmail,
        customerName,
        packageIds:       packages.map(p => p.id),
        ratePerKg:        rate?.ratePerKg || 0,
        whishNumber,
        notes,
        ...(totals || {
          totalWeightLbs: packages.reduce((s, p) => s + parseFloat(p.weight||0), 0).toFixed(2),
          totalWeightKg:  (packages.reduce((s, p) => s + parseFloat(p.weight||0), 0) * 0.453592).toFixed(2),
          shippingCost:   "0.00",
          servicesCost:   "0.00",
          totalDue:       "0.00",
          adminCost:      "0.00",
          profit:         "0.00",
        }),
      });

      // Send shipped email for each package
      for (const pkg of packages) {
        await sendPackageShippedEmail({
          customerEmail:  pkg.customerEmail,
          customerName:   pkg.customerName,
          packageId:      pkg.id,
          description:    pkg.description,
          trackingNumber: tracking.trim(),
        });
      }
      toast(`Shipment created & invoice ${invoiceNumber} generated! Customers notified by email.`);
      onSaved();
      onClose();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Create shipment" onClose={onClose} maxWidth={500}>
      <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
        Grouping <strong>{packages.length} package{packages.length !== 1 ? "s" : ""}</strong> for <strong>{customerName}</strong> into one shipment. An invoice will be generated automatically.
      </p>

      {/* Packages list */}
      <div style={{ background: "var(--bg-soft)", borderRadius: "var(--radius)", padding: "10px 14px", marginBottom: 16 }}>
        {packages.map(p => (
          <div key={p.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: "0.5px solid var(--border)" }}>
            <span style={{ fontFamily: "monospace", color: "var(--text-muted)" }}>{p.id}</span>
            <span style={{ fontWeight: 500 }}>{p.description || "Package"}</span>
            <span style={{ color: "var(--text-muted)" }}>{p.weight} lbs</span>
          </div>
        ))}
      </div>

      <div className="form-row">
        <label>Shipment tracking number (carrier)</label>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={tracking} onChange={e => setTracking(e.target.value)} placeholder="Enter FedEx/UPS/USPS tracking..." style={{ fontFamily: "monospace", flex: 1 }} />
          <button className="btn btn-ghost" style={{ whiteSpace: "nowrap", flexShrink: 0 }} onClick={() => setTracking(generateShipmentId())}>Auto</button>
        </div>
      </div>

      <div className="form-row">
        <label>Your Whish number (shown on invoice)</label>
        <input value={whishNumber} onChange={e => setWhishNumber(e.target.value)} placeholder="+961 XX XXX XXX" />
      </div>

      <div className="form-row">
        <label>Notes</label>
        <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes..." />
      </div>

      {/* Invoice preview */}
      {totals && (
        <div style={{ background: "var(--green-light)", borderRadius: "var(--radius)", padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--green-dark)", marginBottom: 8, textTransform: "uppercase", letterSpacing: ".04em" }}>Invoice preview</div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--green-dark)", marginBottom: 4 }}>
            <span>Total weight</span><span>{totals.totalWeightKg} kg ({totals.totalWeightLbs} lbs)</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--green-dark)", marginBottom: 4 }}>
            <span>Shipping (${rate?.ratePerKg}/kg)</span><span>${totals.shippingCost}</span>
          </div>
          {parseFloat(totals.servicesCost) > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--green-dark)", marginBottom: 4 }}>
              <span>Services</span><span>${totals.servicesCost}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 700, borderTop: "0.5px solid var(--green-mid)", paddingTop: 8, marginTop: 4, color: "var(--green-dark)" }}>
            <span>Total due from customer</span><span>${totals.totalDue}</span>
          </div>
          <div style={{ fontSize: 11, color: "var(--green-dark)", marginTop: 6, opacity: .8 }}>
            Your profit: ${totals.profit} (after $12/kg cost)
          </div>
        </div>
      )}

      {!rate && (
        <div className="error-msg" style={{ fontSize: 12 }}>
          No shipping rate set for this customer. Set one in Shipping Rates before creating the invoice.
        </div>
      )}

      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? "Creating..." : `Ship & generate invoice`}
        </button>
      </div>
    </Modal>
  );
}
